import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const AdminAreas = () => {
  const { t } = useLanguage();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [selectedQrArea, setSelectedQrArea] = useState(null);

  // Edit Area Modal State
  const [editingArea, setEditingArea] = useState(null);
  const [editAreaName, setEditAreaName] = useState('');
  const [editAreaCode, setEditAreaCode] = useState('');
  const [editAreaSubmitting, setEditAreaSubmitting] = useState(false);
  const [editAreaMsg, setEditAreaMsg] = useState('');

  // Modal state for managing multiple representatives
  const [selectedAreaForReps, setSelectedAreaForReps] = useState(null);
  const [repName, setRepName] = useState('');
  const [repDesignation, setRepDesignation] = useState('Municipal Councillor');
  const [repContact, setRepContact] = useState('');
  const [repEmail, setRepEmail] = useState('');
  const [repMsg, setRepMsg] = useState('');
  const [editingRep, setEditingRep] = useState(null);

  // Modal state for managing Sub-Areas / Locations
  const [selectedAreaForSubAreas, setSelectedAreaForSubAreas] = useState(null);
  const [subAreasList, setSubAreasList] = useState([]);
  const [subAreaName, setSubAreaName] = useState('');
  const [subAreaCode, setSubAreaCode] = useState('');
  const [subAreaMsg, setSubAreaMsg] = useState('');
  const [editingSubArea, setEditingSubArea] = useState(null);

  const fetchAreas = useCallback(async () => {
    try {
      const res = await api.get('/areas');
      setAreas(res.data);
    } catch (err) {
      console.error('Error loading area master:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAreas();
  }, [fetchAreas]);

  // Create Area
  const handleCreateArea = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg('');

    try {
      await api.post('/areas', {
        name,
        code
      });

      setName('');
      setCode('');
      setMsg(`${t('createAreaTitle')} - Success!`);
      fetchAreas();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to create Area Master record.');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Edit Area Modal
  const handleOpenEditArea = (area) => {
    setEditingArea(area);
    setEditAreaName(area.name);
    setEditAreaCode(area.code);
    setEditAreaMsg('');
  };

  // Update Area
  const handleUpdateArea = async (e) => {
    e.preventDefault();
    if (!editingArea) return;
    setEditAreaSubmitting(true);
    setEditAreaMsg('');

    try {
      await api.put(`/areas/${editingArea.id}`, {
        name: editAreaName,
        code: editAreaCode
      });

      setMsg(`Area "${editAreaName}" updated successfully!`);
      setEditingArea(null);
      fetchAreas();
    } catch (err) {
      setEditAreaMsg(err.response?.data?.message || 'Failed to update Area.');
    } finally {
      setEditAreaSubmitting(false);
    }
  };

  // Delete Area
  const handleDelete = async (id) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await api.delete(`/areas/${id}`);
      fetchAreas();
    } catch (err) {
      alert('Failed to delete Area record.');
    }
  };

  // Add or Update Representative
  const handleSaveRepresentative = async (e) => {
    e.preventDefault();
    if (!selectedAreaForReps) return;
    setRepMsg('');

    try {
      if (editingRep) {
        // Update existing rep
        await api.put(`/areas/representatives/${editingRep.id}`, {
          name: repName,
          designation: repDesignation,
          contact_info: repContact,
          email: repEmail
        });
        setRepMsg(`${t('updateOfficialBtn')} - Success!`);
        setEditingRep(null);
      } else {
        // Add new rep
        await api.post(`/areas/${selectedAreaForReps.id}/representatives`, {
          name: repName,
          designation: repDesignation,
          contact_info: repContact,
          email: repEmail
        });
        setRepMsg('Representative added successfully!');
      }

      setRepName('');
      setRepContact('');
      setRepEmail('');
      setRepDesignation('Municipal Councillor');
      
      const repsRes = await api.get(`/areas/${selectedAreaForReps.id}/representatives`);
      setSelectedAreaForReps(prev => ({ ...prev, representatives: repsRes.data }));
      fetchAreas();
    } catch (err) {
      setRepMsg(err.response?.data?.message || 'Failed to save representative.');
    }
  };

  const handleStartEditRep = (rep) => {
    setEditingRep(rep);
    setRepName(rep.name);
    setRepDesignation(rep.designation);
    setRepContact(rep.contact_info || '');
    setRepEmail(rep.email || '');
    setRepMsg('');
  };

  const handleCancelEditRep = () => {
    setEditingRep(null);
    setRepName('');
    setRepContact('');
    setRepEmail('');
    setRepDesignation('Municipal Councillor');
  };

  const handleDeleteRepresentative = async (repId) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await api.delete(`/areas/representatives/${repId}`);
      const repsRes = await api.get(`/areas/${selectedAreaForReps.id}/representatives`);
      setSelectedAreaForReps(prev => ({ ...prev, representatives: repsRes.data }));
      fetchAreas();
    } catch (err) {
      alert('Failed to remove representative.');
    }
  };

  // Sub-Areas Handlers
  const openSubAreasModal = async (area) => {
    setSelectedAreaForSubAreas(area);
    setSubAreaMsg('');
    setEditingSubArea(null);
    setSubAreaName('');
    setSubAreaCode('');
    try {
      const res = await api.get(`/sub-areas?area_id=${area.id}`);
      setSubAreasList(res.data);
    } catch (err) {
      console.error('Error fetching sub-areas:', err);
    }
  };

  const handleSaveSubArea = async (e) => {
    e.preventDefault();
    if (!selectedAreaForSubAreas) return;
    setSubAreaMsg('');

    try {
      if (editingSubArea) {
        // Update
        await api.put(`/sub-areas/${editingSubArea.id}`, {
          area_id: selectedAreaForSubAreas.id,
          name: subAreaName,
          code: subAreaCode
        });
        setSubAreaMsg(`${t('updateSubAreaBtn')} - Success!`);
        setEditingSubArea(null);
      } else {
        // Create
        await api.post('/sub-areas', {
          area_id: selectedAreaForSubAreas.id,
          name: subAreaName,
          code: subAreaCode
        });
        setSubAreaMsg('Sub-Area / Location created successfully!');
      }

      setSubAreaName('');
      setSubAreaCode('');
      
      const res = await api.get(`/sub-areas?area_id=${selectedAreaForSubAreas.id}`);
      setSubAreasList(res.data);
    } catch (err) {
      setSubAreaMsg(err.response?.data?.message || 'Failed to save Sub-Area.');
    }
  };

  const handleStartEditSubArea = (sa) => {
    setEditingSubArea(sa);
    setSubAreaName(sa.name);
    setSubAreaCode(sa.code || '');
    setSubAreaMsg('');
  };

  const handleCancelEditSubArea = () => {
    setEditingSubArea(null);
    setSubAreaName('');
    setSubAreaCode('');
  };

  const handleDeleteSubArea = async (subAreaId) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await api.delete(`/sub-areas/${subAreaId}`);
      const res = await api.get(`/sub-areas?area_id=${selectedAreaForSubAreas.id}`);
      setSubAreasList(res.data);
    } catch (err) {
      alert('Failed to delete Sub-Area.');
    }
  };

  const getPublicUrl = (areaId) => {
    return `${window.location.origin}/public-complaint?area_id=${areaId}`;
  };

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h2 className="brand-font fw-bold mb-1">{t('areaMaster')} & {t('subLocationsMaster')}</h2>
        <p className="text-muted mb-0">Create, edit, and manage Wards, Sublocations, elected officials, and public QR posters</p>
      </div>

      {msg && <div className="alert alert-info py-2 small mb-4 alert-dismissible fade show">{msg}</div>}

      <div className="row g-4">
        {/* Create Area Form */}
        <div className="col-lg-4">
          <div className="card glass-card border-0 p-4 sticky-top" style={{ top: '90px' }}>
            <h5 className="brand-font fw-bold mb-3 d-flex align-items-center">
              <i className="bi bi-geo-alt-fill text-primary me-2"></i>
              {t('createAreaTitle')}
            </h5>
            <form onSubmit={handleCreateArea}>
              <div className="mb-3">
                <label className="form-label fw-semibold small">{t('areaName')} *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Ward 12 - Dadar West"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="form-label fw-semibold small">{t('areaCode')} *</label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  placeholder="e.g. W-12"
                  maxLength={10}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary-gradient w-100 py-2.5" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-1"></i>
                    {t('createAreaTitle')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Areas List Table */}
        <div className="col-lg-8">
          <div className="card glass-card border-0 p-4">
            <h5 className="brand-font fw-bold mb-3 d-flex align-items-center justify-content-between">
              <span>
                <i className="bi bi-map-fill text-primary me-2"></i>
                {t('areaMaster')} Directory ({areas.length})
              </span>
            </h5>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : areas.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-geo fs-1 d-block mb-2 text-muted"></i>
                <p className="mb-0">No Area Master records defined yet.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t('areaCode')}</th>
                      <th>{t('areaName')}</th>
                      <th>{t('wardOfficialsTitle')}</th>
                      <th className="text-end">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areas.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <span className="badge bg-primary font-monospace px-2.5 py-1.5">{a.code}</span>
                        </td>
                        <td className="fw-semibold text-dark">{a.name}</td>
                        <td>
                          {a.representatives && a.representatives.length > 0 ? (
                            <div className="d-flex flex-wrap gap-1">
                              {a.representatives.map(r => (
                                <span key={r.id} className="badge bg-light text-dark border p-1.5">
                                  <i className="bi bi-person-badge text-primary me-1"></i>
                                  <strong>{r.name}</strong> ({r.designation})
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted small italic">No officials assigned</span>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              onClick={() => handleOpenEditArea(a)}
                              className="btn btn-outline-primary"
                              title={t('editAreaTitle')}
                            >
                              <i className="bi bi-pencil-square me-1"></i>
                              {t('edit')}
                            </button>
                            <button
                              onClick={() => openSubAreasModal(a)}
                              className="btn btn-outline-info"
                              title={t('subLocationsMaster')}
                            >
                              <i className="bi bi-geo-alt-fill me-1"></i> Sub-Locations
                            </button>
                            <button
                              onClick={() => { setSelectedAreaForReps(a); handleCancelEditRep(); }}
                              className="btn btn-outline-success"
                              title={t('wardOfficialsTitle')}
                            >
                              <i className="bi bi-people-fill me-1"></i> Officials ({a.representatives?.length || 0})
                            </button>
                            <button
                              onClick={() => setSelectedQrArea(a)}
                              className="btn btn-outline-secondary"
                              title="Public QR Poster"
                            >
                              <i className="bi bi-qr-code"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(a.id)}
                              className="btn btn-outline-danger"
                              title="Delete Area"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EDIT AREA MODAL */}
      {editingArea && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded-4">
              <div className="modal-header border-bottom bg-light">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-pencil-square text-primary me-2"></i>
                  {t('editAreaTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingArea(null)}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleUpdateArea}>
                <div className="modal-body p-4">
                  {editAreaMsg && <div className="alert alert-danger py-2 small mb-3">{editAreaMsg}</div>}

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('areaName')} *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editAreaName}
                      onChange={(e) => setEditAreaName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('areaCode')} *</label>
                    <input
                      type="text"
                      className="form-control text-uppercase"
                      value={editAreaCode}
                      maxLength={10}
                      onChange={(e) => setEditAreaCode(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="modal-footer border-top bg-light">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setEditingArea(null)}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary-gradient px-4"
                    disabled={editAreaSubmitting}
                  >
                    {editAreaSubmitting ? 'Saving...' : t('updateAreaBtn')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Areas / Locations Modal */}
      {selectedAreaForSubAreas && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-geo-alt-fill me-2"></i> {t('subLocationsMaster')} — {selectedAreaForSubAreas.name} ({selectedAreaForSubAreas.code})
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedAreaForSubAreas(null)}
                ></button>
              </div>
              <div className="modal-body p-4">
                {subAreaMsg && <div className="alert alert-info py-2 small">{subAreaMsg}</div>}

                {/* Sub-Areas List */}
                <h6 className="fw-bold mb-3 text-secondary">{t('subLocationsMaster')} Directory</h6>
                {subAreasList.length > 0 ? (
                  <div className="table-responsive mb-4">
                    <table className="table table-sm table-hover align-middle mb-0 border">
                      <thead className="table-light">
                        <tr>
                          <th>{t('subAreaCodeLabel')}</th>
                          <th>{t('subAreaNameLabel')}</th>
                          <th className="text-end">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subAreasList.map(sa => (
                          <tr key={sa.id}>
                            <td>
                              <span className="badge bg-secondary font-monospace">{sa.code || 'N/A'}</span>
                            </td>
                            <td className="fw-semibold text-dark">{sa.name}</td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm">
                                <button
                                  onClick={() => handleStartEditSubArea(sa)}
                                  className="btn btn-outline-primary py-0 px-2"
                                  title={t('editSubAreaTitle')}
                                >
                                  <i className="bi bi-pencil-square me-1"></i> {t('edit')}
                                </button>
                                <button
                                  onClick={() => handleDeleteSubArea(sa.id)}
                                  className="btn btn-outline-danger py-0 px-2"
                                  title="Remove Sublocation"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted small mb-4">No sub-areas added for this ward yet.</p>
                )}

                {/* Add / Edit Sub-Area Form */}
                <div className="card bg-light border p-3 rounded-3">
                  <h6 className="fw-bold text-dark mb-3">
                    {editingSubArea ? (
                      <span className="text-primary"><i className="bi bi-pencil-square me-1"></i>{t('editSubAreaTitle')}</span>
                    ) : (
                      <span><i className="bi bi-plus-circle me-1"></i>{t('createSubAreaTitle')}</span>
                    )}
                  </h6>
                  <form onSubmit={handleSaveSubArea} className="row g-3">
                    <div className="col-md-7">
                      <label className="form-label small fw-semibold">{t('subAreaNameLabel')} *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="e.g. Station Market Road / Sector 4"
                        value={subAreaName}
                        onChange={e => setSubAreaName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label small fw-semibold">{t('subAreaCodeLabel')}</label>
                      <input
                        type="text"
                        className="form-control form-control-sm text-uppercase"
                        placeholder="e.g. SUB-101"
                        value={subAreaCode}
                        onChange={e => setSubAreaCode(e.target.value)}
                      />
                    </div>
                    <div className="col-12 d-flex justify-content-end gap-2">
                      {editingSubArea && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleCancelEditSubArea}
                        >
                          {t('cancel')}
                        </button>
                      )}
                      <button type="submit" className="btn btn-info text-white btn-sm fw-bold px-3">
                        <i className="bi bi-check2-circle me-1"></i>
                        {editingSubArea ? t('updateSubAreaBtn') : t('createSubAreaTitle')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedAreaForSubAreas(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Representatives Modal */}
      {selectedAreaForReps && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-people-fill me-2 text-primary"></i> {t('wardOfficialsTitle')} — {selectedAreaForReps.name} ({selectedAreaForReps.code})
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => { setSelectedAreaForReps(null); handleCancelEditRep(); }}
                ></button>
              </div>
              <div className="modal-body p-4">
                {repMsg && <div className="alert alert-info py-2 small">{repMsg}</div>}

                {/* List of Existing Representatives */}
                <h6 className="fw-bold mb-3 text-secondary">Current Officials Assigned to Ward</h6>
                {selectedAreaForReps.representatives && selectedAreaForReps.representatives.length > 0 ? (
                  <div className="list-group mb-4">
                    {selectedAreaForReps.representatives.map(r => (
                      <div key={r.id} className="list-group-item d-flex justify-content-between align-items-center p-3">
                        <div>
                          <strong className="text-primary fs-6">{r.name}</strong>
                          <span className="badge bg-secondary ms-2">{r.designation}</span>
                          <small className="d-block text-muted mt-1">
                            <i className="bi bi-telephone me-1"></i> {r.contact_info || 'N/A'} {r.email && `| Email: ${r.email}`}
                          </small>
                        </div>
                        <div className="btn-group btn-group-sm">
                          <button
                            onClick={() => handleStartEditRep(r)}
                            className="btn btn-outline-primary"
                            title={t('editOfficialTitle')}
                          >
                            <i className="bi bi-pencil-square me-1"></i> {t('edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteRepresentative(r.id)}
                            className="btn btn-outline-danger"
                            title="Remove Official"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small mb-4">No representatives added yet for this ward.</p>
                )}

                {/* Add / Edit Representative Form */}
                <div className="card bg-light border p-3 rounded-3">
                  <h6 className="fw-bold text-dark mb-3">
                    {editingRep ? (
                      <span className="text-primary"><i className="bi bi-pencil-square me-1"></i>{t('editOfficialTitle')}</span>
                    ) : (
                      <span><i className="bi bi-plus-circle me-1"></i>{t('addOfficialTitle')}</span>
                    )}
                  </h6>
                  <form onSubmit={handleSaveRepresentative} className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">{t('officialName')} *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="e.g. Hon. Sunita Patil"
                        value={repName}
                        onChange={e => setRepName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">{t('officialDesignation')} *</label>
                      <select
                        className="form-select form-select-sm"
                        value={repDesignation}
                        onChange={e => setRepDesignation(e.target.value)}
                        required
                      >
                        <option value="Municipal Councillor">Municipal Councillor</option>
                        <option value="MLA (Member of Legislative Assembly)">MLA (Member of Legislative Assembly)</option>
                        <option value="MP (Member of Parliament)">MP (Member of Parliament)</option>
                        <option value="Assistant Municipal Commissioner">Assistant Municipal Commissioner</option>
                        <option value="Ward Officer">Ward Officer</option>
                        <option value="Elected Official">Elected Official</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">{t('officialPhone')}</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="e.g. +91 98200 11222"
                        value={repContact}
                        onChange={e => setRepContact(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">{t('officialEmail')}</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        placeholder="rep@gov.in"
                        value={repEmail}
                        onChange={e => setRepEmail(e.target.value)}
                      />
                    </div>
                    <div className="col-12 d-flex justify-content-end gap-2">
                      {editingRep && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleCancelEditRep}
                        >
                          {t('cancel')}
                        </button>
                      )}
                      <button type="submit" className="btn btn-success btn-sm fw-bold px-3">
                        <i className="bi bi-check2-circle me-1"></i>
                        {editingRep ? t('updateOfficialBtn') : t('addOfficialTitle')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setSelectedAreaForReps(null); handleCancelEditRep(); }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Poster Modal */}
      {selectedQrArea && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-qr-code-scan me-2"></i> Public QR Code Poster
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedQrArea(null)}
                ></button>
              </div>
              <div className="modal-body text-center p-4">
                <div className="border border-3 border-primary p-4 rounded-3 bg-light shadow-sm">
                  <span className="badge bg-primary fs-6 px-3 py-2 text-uppercase mb-2">Public Complaint Point</span>
                  <h3 className="fw-bold text-dark">{selectedQrArea.name}</h3>
                  <p className="text-muted small mb-3">Area Code: <strong>{selectedQrArea.code}</strong></p>

                  <div className="bg-white p-3 d-inline-block rounded border shadow-sm mb-3">
                    <QRCodeCanvas
                      value={getPublicUrl(selectedQrArea.id)}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>

                  <p className="fw-bold text-secondary mb-1">Scan with any Mobile Camera to Register Complaint</p>
                  <small className="text-muted d-block mb-3">No account or password registration required</small>

                  {/* List Ward Elected Officials */}
                  {selectedQrArea.representatives && selectedQrArea.representatives.length > 0 && (
                    <div className="bg-white p-2 border rounded text-start small text-muted">
                      <strong className="d-block text-dark mb-1"><i className="bi bi-people-fill text-primary me-1"></i> Ward Elected Officials:</strong>
                      {selectedQrArea.representatives.map(r => (
                        <div key={r.id} className="border-bottom pb-1 mb-1">
                          <span className="fw-bold text-dark">{r.name}</span> ({r.designation})
                          {r.contact_info && <small className="d-block text-muted"><i className="bi bi-telephone-fill text-primary me-1"></i>{r.contact_info}</small>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-3 text-start small">
                  <strong className="text-muted">Target Link:</strong>
                  <input type="text" className="form-control form-control-sm mt-1" readOnly value={getPublicUrl(selectedQrArea.id)} />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedQrArea(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary fw-bold"
                  onClick={() => window.print()}
                >
                  <i className="bi bi-printer-fill me-1"></i> Print QR Poster
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAreas;
